import { buildAutoToffSearchQuery, autoToffSearchDestination } from '@theme/autotoff-search';
import { Component } from '@theme/component';

class AutoToffFinder extends Component {
  #controller;
  #models = [];
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    try { this.#models = JSON.parse(this.querySelector('[data-at-models]')?.textContent || '[]'); } catch { this.#models = []; }
    try {
      const extraModels = JSON.parse(this.querySelector('[data-at-extra-models]')?.textContent || '[]');
      const key = title => title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const titles = new Set(this.#models.map(item => key(item.title)));
      for (const item of extraModels) {
        if (titles.has(key(item.title))) continue;
        this.#models.push(item); titles.add(key(item.title));
      }
    } catch {}
    const brand = this.querySelector('[data-at-brand]');
    const model = this.querySelector('[data-at-model]');
    const generation = this.querySelector('[data-at-generation]');
    const form = this.querySelector('form');
    if (!(brand instanceof HTMLSelectElement) || !(model instanceof HTMLSelectElement) || !(generation instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;
    const allModelsLabel = model.options[0]?.text || '';
    const populate = () => {
      const chosenBrand = brand.value.split('-')[0];
      model.replaceChildren(new Option(allModelsLabel, ''));
      for (const item of this.#models.filter(item => item.brand === chosenBrand).sort((a, b) => a.title.localeCompare(b.title, 'ro', { numeric: true, sensitivity: 'base' }))) {
        const destination = new URL(item.url || form.action, location.origin);
        if (!item.url) {
          destination.searchParams.set('type', 'product');
          destination.searchParams.set('q', item.query);
          destination.searchParams.set('options[prefix]', 'none');
        }
        const option = new Option(item.title.replace(`${item.brand} `, ''), item.url || destination.href);
        option.dataset.query = item.query || item.title;
        model.add(option);
      }
      model.disabled = !brand.value || model.options.length === 1;
      generation.disabled = !brand.value;
    };
    const persist = () => {
      try { localStorage.setItem('autotoff-vehicle-v1', JSON.stringify({ brand: brand.value, model: model.value, generation: generation.value.trim() })); } catch { /* Search remains usable without storage. */ }
    };
    for (const label of this.querySelectorAll('[data-at-enhanced]')) label.hidden = false;
    populate();
    try {
      const saved = JSON.parse(localStorage.getItem('autotoff-vehicle-v1') || 'null');
      if (saved && [...brand.options].some(option => option.value === saved.brand)) {
        brand.value = saved.brand; populate();
        if ([...model.options].some(option => option.value === saved.model)) model.value = saved.model;
        generation.value = typeof saved.generation === 'string' ? saved.generation.slice(0, 30) : '';
      }
    } catch { /* Default controls remain available. */ }
    brand.addEventListener('change', () => { populate(); generation.value = ''; persist(); }, { signal: this.#controller.signal });
    model.addEventListener('change', () => { generation.value = ''; persist(); }, { signal: this.#controller.signal });
    generation.addEventListener('change', persist, { signal: this.#controller.signal });
    form.addEventListener('submit', event => {
      if (!brand.value) return;
      event.preventDefault();
      persist();
      const selectedModel = model.selectedOptions[0];
      const searchTerm = generation.value.trim();
      let destination;
      if (!searchTerm) {
        destination = new URL(model.value || brand.selectedOptions[0].dataset.url || form.action, location.origin);
      } else {
        destination = new URL(form.action, location.origin);
        destination.searchParams.set('type', 'product');
        destination.searchParams.set('q', `${selectedModel?.dataset.query || brand.value} ${searchTerm}`);
      }
      if (destination.origin !== location.origin) return;
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) destination.searchParams.set('preview_theme_id', preview);
      location.assign(destination.href);
    }, { signal: this.#controller.signal });
  }
  disconnectedCallback() { this.#controller?.abort(); super.disconnectedCallback(); }
}

class AutoToffSaved extends Component {
  #controller;
  #products = [];
  #opener;
  #storageError = false;
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#load();
    const options = { signal: this.#controller.signal };
    document.addEventListener('click', this.#onClick, options);
    window.addEventListener('storage', () => { this.#load(); this.#refresh(); }, options);
    document.addEventListener('shopify:section:load', () => this.#refresh(), options);
    const dialog = this.querySelector('dialog');
    dialog?.addEventListener('close', () => this.#opener?.focus(), options);
    this.#refresh();
    this.#observer.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  }
  #observer = new MutationObserver(() => this.#syncControls());
  #load() {
    try {
      const data = JSON.parse(localStorage.getItem('autotoff-saved-v1') || '[]');
      this.#products = Array.isArray(data) ? data.filter(item => typeof item?.handle === 'string' && typeof item?.title === 'string' && typeof item?.url === 'string' && item.url.startsWith('/') && !item.url.startsWith('//')).slice(0, 100) : [];
    } catch { this.#storageError = true; }
  }
  #persist() {
    try { localStorage.setItem('autotoff-saved-v1', JSON.stringify(this.#products)); } catch { this.#storageError = true; }
    if (this.#storageError) {
      const status = this.querySelector('[data-at-saved-status]');
      if (status) status.textContent = this.dataset.error || '';
    }
  }
  #syncControls() {
    for (const button of document.querySelectorAll('[data-at-save]')) {
      button.hidden = false;
      button.setAttribute('aria-pressed', String(this.#products.some(item => item.handle === button.dataset.atSave)));
    }
    for (const button of document.querySelectorAll('[data-at-open-saved]')) button.hidden = false;
    for (const count of document.querySelectorAll('[data-at-saved-count]')) {
      const value = String(this.#products.length);
      if (count.textContent !== value) count.textContent = value;
    }
  }
  #refresh() {
    this.#syncControls();
    const list = this.querySelector('[data-at-saved-list]');
    if (!list) return;
    list.replaceChildren();
    if (!this.#products.length) {
      const message = document.createElement('p');
      message.className = 'at-saved-empty';
      message.textContent = this.dataset.empty || '';
      list.append(message);
      return;
    }
    for (const product of this.#products) {
      const row = document.createElement('div'); row.className = 'at-saved-row';
      const link = document.createElement('a'); link.href = product.url; link.textContent = product.title;
      const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.atRemove = product.handle;
      remove.textContent = this.dataset.remove || ''; remove.setAttribute('aria-label', `${this.dataset.remove} ${product.title}`);
      row.append(link, remove); list.append(row);
    }
  }
  #onClick = event => {
    if (!(event.target instanceof Element)) return;
    const save = event.target.closest('[data-at-save]');
    if (save instanceof HTMLButtonElement) {
      event.preventDefault(); event.stopPropagation();
      const handle = save.dataset.atSave;
      if (!handle) return;
      if (this.#products.some(item => item.handle === handle)) this.#products = this.#products.filter(item => item.handle !== handle);
      else if (this.#products.length < 100) this.#products.push({ handle, title: save.dataset.atTitle || '', url: save.dataset.atUrl || '' });
      this.#persist(); this.#refresh(); return;
    }
    const open = event.target.closest('[data-at-open-saved]');
    const dialog = this.querySelector('dialog');
    if (open && dialog instanceof HTMLDialogElement) { this.#opener = open; this.#refresh(); dialog.showModal(); return; }
    if (event.target.closest('[data-at-close-saved]') && dialog instanceof HTMLDialogElement) { dialog.close(); return; }
    const remove = event.target.closest('[data-at-remove]');
    if (remove instanceof HTMLButtonElement) {
      this.#products = this.#products.filter(item => item.handle !== remove.dataset.atRemove);
      this.#persist(); this.#refresh();
      this.querySelector('[data-at-saved-list] button')?.focus();
    }
  };
  disconnectedCallback() { this.#controller?.abort(); this.#observer.disconnect(); super.disconnectedCallback(); }
}

if (!customElements.get('autotoff-finder')) customElements.define('autotoff-finder', AutoToffFinder);
if (!customElements.get('autotoff-saved')) customElements.define('autotoff-saved', AutoToffSaved);

if (!window.Shopify?.designMode) {
  const preventImageSave = event => {
    const imageTarget = event.composedPath().some(node => node instanceof HTMLImageElement);
    const imageLinkDrag = event.type === 'dragstart'
      && event.target instanceof HTMLAnchorElement
      && event.target.querySelector('img');
    if (imageTarget || imageLinkDrag) event.preventDefault();
  };
  document.addEventListener('contextmenu', preventImageSave, { capture: true });
  document.addEventListener('dragstart', preventImageSave, { capture: true });
}

document.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.closest('autotoff-finder')) return;
  const input = form.querySelector('input[type="search"][name="q"]');
  if (!(input instanceof HTMLInputElement) || !new URL(form.action, location.origin).pathname.endsWith('/search')) return;
  const query = buildAutoToffSearchQuery(input.value);
  if (query === input.value.trim()) return;
  const destination = autoToffSearchDestination(form, query);
  if (!destination) return;
  event.preventDefault();
  event.stopPropagation();
  location.assign(destination.href);
}, { capture: true });
